/**
 * @module components/modals/PdfExportModal
 * @summary The export dialog for the vector PDF sheet set.
 * @description
 *   The one thing this dialog has to do is tell the truth BEFORE the download:
 *   what scale the layout will land at, whether it fits the sheet, and how many
 *   sheets the supplier is about to receive. Those numbers come from
 *   `planExport`, the same function the exporter itself uses, so the preview
 *   cannot disagree with the file.
 *
 *   The choices are remembered. A drawing office issues at one paper size and
 *   one status for months at a time, and re-picking them on every issue is how
 *   a sheet goes out stamped FOR CONSTRUCTION by accident.
 *
 *   This dialog is for the 2D LAYOUT — the measurable engineering document. The
 *   3D view has its own, `Pdf3dExportModal`, producing a deliberately different
 *   deliverable; see `export3dPdf` for why they are not one dialog with a
 *   switch.
 */
import { useEffect, useState } from 'react';
import { engine } from '../../engine/CanvasEngine';
import { planExport, type PdfExportOptions } from '../../engine/export/exportPdf';
import { PAPERS, SCALE_LADDER, type PaperName } from '../../engine/export/pdf/sheet';
import { lsGet, lsSet } from '../../lib/storage';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Remembered between sessions — see the note about accidental re-stamping. */
const PREFS_KEY = 'jcc_pdf_export_v1';

/**
 * Issue statuses, in the order a drawing travels through them. The wording is
 * deliberately the standard set: a supplier reading FOR TENDER knows they are
 * pricing, and reading FOR CONSTRUCTION knows they may cut steel.
 */
const STATUSES = [
  'ISSUED FOR QUOTATION',
  'ISSUED FOR TENDER',
  'ISSUED FOR APPROVAL',
  'ISSUED FOR CONSTRUCTION',
  'FOR INFORMATION ONLY',
  'PRELIMINARY',
  'AS BUILT',
  '',
] as const;

interface Prefs {
  paper: PaperName;
  orientation: 'landscape' | 'portrait';
  /** `'auto'`, `'fit'`, or a ladder denominator as a string. */
  scale: string;
  status: string;
  watermark: boolean;
  northDeg: number;
  includeSchedule: boolean;
  scheduleCsvToo: boolean;
}

const DEFAULT_PREFS: Prefs = {
  paper: 'A1',
  orientation: 'landscape',
  scale: 'auto',
  status: 'ISSUED FOR QUOTATION',
  watermark: false,
  northDeg: 0,
  includeSchedule: true,
  scheduleCsvToo: false,
};

/** Turn the dialog's state into what the exporter takes. */
function toOptions(p: Prefs): PdfExportOptions {
  return {
    paper: p.paper,
    orientation: p.orientation,
    fit: p.scale === 'fit',
    scale: p.scale === 'auto' || p.scale === 'fit' ? 'auto' : Number(p.scale),
    status: p.status,
    // A watermark is for a sheet nobody may build from, so it repeats the status
    // rather than inventing a second word for the same thing.
    watermark: p.watermark ? p.status || 'PRELIMINARY' : undefined,
    northDeg: p.northDeg,
    includeSchedule: p.includeSchedule,
    scheduleCsvToo: p.scheduleCsvToo,
  };
}

export function PdfExportModal({ open, onClose }: Props) {
  const [p, setP] = useState<Prefs>(() => ({ ...DEFAULT_PREFS, ...lsGet(PREFS_KEY, {}) }));
  const set = <K extends keyof Prefs>(k: K, v: Prefs[K]) => setP((q) => ({ ...q, [k]: v }));
  // Mirrored rather than derived: this one lives on the DOCUMENT, not in the
  // remembered dialog preferences, so it is re-read whenever the dialog opens.
  const [scaleMm, setScaleMm] = useState(() => engine.getScaleMm());

  useEffect(() => {
    if (open) setScaleMm(engine.getScaleMm());
  }, [open]);

  useEffect(() => {
    if (open) lsSet(PREFS_KEY, p);
  }, [p, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Recomputed on every render rather than memoised. Most of what the plan
  // depends on — the shapes, the document scale — lives on the engine rather
  // than in props, so a dependency array would be a list of lies; and the cost
  // is one pass over the shapes, on a dialog that only re-renders when somebody
  // clicks something. Always correct beats nominally cheaper.
  const plan = planExport(toOptions(p));

  const go = () => {
    engine.exportPdf(toOptions(p));
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="pdfx" onClick={(e) => e.stopPropagation()}>
        <div className="pdfx-head">
          <div>
            <div className="pdfx-title">Export PDF sheet set</div>
            <div className="pdfx-sub">
              Vector sheets — sharp at any zoom, plotted to a standard scale
            </div>
          </div>
          <button className="pdfx-x" title="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* ── What you are about to get ── */}
        <div className={'pdfx-plan' + (plan && !plan.fits ? ' warn' : '')}>
          {!plan ? (
            <span>Nothing on the canvas to export.</span>
          ) : (
            <>
              <span className="pdfx-plan-big">{plan.scaleText}</span>
              <span className="pdfx-plan-rest">
                on {plan.paper} {plan.orientation === 'landscape' ? 'landscape' : 'portrait'} ·{' '}
                {plan.sheets} sheet{plan.sheets > 1 ? 's' : ''}
                {plan.scheduleRows > 0 && <> · {plan.scheduleRows} scheduled items</>}
                <br />
                Plant {plan.realW.toFixed(1)} × {plan.realH.toFixed(1)} m
                {!plan.fits && (
                  <>
                    <br />
                    <strong>
                      This scale is bigger than the sheet — the drawing will be clipped. Pick a
                      coarser scale or a larger sheet.
                    </strong>
                  </>
                )}
              </span>
            </>
          )}
        </div>

        {/* ── Paper ── */}
        <label className="pdfx-lbl">Sheet size</label>
        <div className="pdfx-seg">
          {(Object.keys(PAPERS) as PaperName[]).map((n) => (
            <button
              key={n}
              className={p.paper === n ? 'on' : ''}
              onClick={() => set('paper', n)}
              title={`${PAPERS[n].h} × ${PAPERS[n].w} mm landscape`}
            >
              {n}
            </button>
          ))}
        </div>

        <label className="pdfx-lbl">Orientation</label>
        <div className="pdfx-seg">
          <button
            className={p.orientation === 'landscape' ? 'on' : ''}
            onClick={() => set('orientation', 'landscape')}
          >
            Landscape
          </button>
          <button
            className={p.orientation === 'portrait' ? 'on' : ''}
            onClick={() => set('orientation', 'portrait')}
          >
            Portrait
          </button>
        </div>

        {/* ── Scale ── */}
        <div className="pdfx-row">
          <div className="pdfx-col">
            <label className="pdfx-lbl">Plot scale</label>
            <select value={p.scale} onChange={(e) => set('scale', e.target.value)}>
              <option value="auto">Auto — tightest standard scale that fits</option>
              <option value="fit">Fit to sheet (marked NTS)</option>
              {SCALE_LADDER.filter((r) => r >= 10).map((r) => (
                <option key={r} value={String(r)}>
                  1:{r}
                </option>
              ))}
            </select>
          </div>
          <div className="pdfx-col narrow">
            <label className="pdfx-lbl" title="Bearing of north, clockwise from up the sheet">
              North (°)
            </label>
            <input
              type="number"
              min={0}
              max={359}
              step={5}
              value={p.northDeg}
              onChange={(e) => set('northDeg', (((Number(e.target.value) || 0) % 360) + 360) % 360)}
            />
          </div>
        </div>

        {/* The drawing's own scale. Everything above is meaningless if this is
            wrong: a sheet marked 1:50 built on the wrong mm-per-unit is not a
            rounding error, it is a false statement a supplier will measure off.
            It lives on the document, so changing it here changes the drawing. */}
        <label className="pdfx-lbl">
          Drawing scale — millimetres per drawing unit
          <span className="pdfx-hint">
            {' '}
            the basis for every dimension; saved with the document
          </span>
        </label>
        <div className="pdfx-mm">
          <input
            type="number"
            min={0.1}
            max={5000}
            step={1}
            value={scaleMm}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isFinite(v) || v <= 0) return;
              setScaleMm(v);
              engine.setScaleMm(v);
            }}
          />
          <span className="pdfx-mm-note">
            one grid square (20 units) = {((20 * scaleMm) / 1000).toFixed(2)} m
          </span>
        </div>

        {/* ── Status ── */}
        <label className="pdfx-lbl">Issue status</label>
        <select value={p.status} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => (
            <option key={s || 'none'} value={s}>
              {s || '— none —'}
            </option>
          ))}
        </select>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.watermark}
            onChange={(e) => set('watermark', e.target.checked)}
          />
          <span>
            Overprint the status across the sheet
            <em> — for a drawing nobody may build from yet</em>
          </span>
        </label>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.includeSchedule}
            onChange={(e) => set('includeSchedule', e.target.checked)}
          />
          <span>
            Equipment schedule sheet
            <em> — tags, quantities and sizes; suppliers quote from this</em>
          </span>
        </label>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.scheduleCsvToo}
            disabled={!p.includeSchedule}
            onChange={(e) => set('scheduleCsvToo', e.target.checked)}
          />
          <span>
            Also download the schedule as CSV
            <em> — for a supplier who would rather have a spreadsheet</em>
          </span>
        </label>

        <div className="pdfx-btns">
          <button className="pdfx-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="pdfx-btn primary" disabled={!plan} onClick={go}>
            Export {plan ? `${plan.sheets} sheet${plan.sheets > 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
